import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { fetchHousePreDtl } from '../../infra/api/mtg/fetchHousePreDtl';
import { addUnitToMaxAmount, currency, currencyKR } from '../helper/filter';
import { useBaseParam } from '../hooks/useBaseParam';
import useBeacon from '../hooks/useBeacon';
import useNav from '../hooks/useNav';
import { beaconSelector } from '../recoil/selector';
import { PAGE_NM } from '../types/types';
import useRateCalc from './useRateCalc';

export const useMtgDetail = () => {
  const { requestSeq: requestSeq } = useParams();
  const beaconData = useRecoilValue(beaconSelector);
  const { calculate } = useRateCalc();

  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.MTG_DETAIL,
  });

  const sendBeaconClkBtn = (subEventNm: string, properties: any) => {
    beacon.sendClkBtn({
      events: { subEventName: subEventNm },
      properties:
        typeof properties === 'string' ? { btnNm: properties } : properties,
    });
  };

  const [preDtl, setPreDtl] = useState<any>({});
  const location: any = useLocation();
  const { preRequestDtlNo } = useParams();
  const fetchPreDtl = useBaseParam(fetchHousePreDtl, {
    preRequestDtlNo: preRequestDtlNo,
  });
  const getPreDtl = async () => {
    const response = await fetchPreDtl();
    setPreDtl(response.data.result);
  };

  useEffect(() => {
    beacon.sendViewPage();
    getPreDtl();
  }, []);

  const [mtgInfo, setMtgInfo] = useState<any>({});
  const [calcRate, setCalcRate] = useState<any[]>([]);
  const [doc, setDoc] = useState<string>('');
  const [productOptionsSummary, setProductOptionsSummary] = useState<any[]>([]);
  const [productOptionsDetail, setProductOptionsDetail] = useState<any[]>([]);
  const [stepList, setStepList] = useState<any[]>([]);
  const [applyPrimeRate, setApplyPrimeRate] = useState<string>('0');

  const getYearMonthNotation = (term?: number): string => {
    if (!term || isNaN(term)) return '';

    const year = term / 12;
    const month = term % 12;

    return `${year}년${month > 0 ? ' ' + month + '개월' : ''}`;
  };

  const [calcAmount, setCalcAmount] = useState<number>(0);
  const [calcTerm, setCalcTerm] = useState<number>(0);
  const [calcInterest, setCalcInterest] = useState<number>(0);

  useEffect(() => {
    // 정보를 loading 했으면 이자를 계산한다.
    const { housePreRequestInfoData, houseCustProductInfo } = preDtl || {};
    if (!houseCustProductInfo?.houseProductInfo) return;

    const productInfo = houseCustProductInfo.houseProductInfo;

    const amount =
      houseCustProductInfo?.housePreProductInfo?.amount ||
      housePreRequestInfoData?.hopefulAmt ||
      0;
    const interest =
      houseCustProductInfo?.housePreProductInfo?.interestRate || 0;
    const term = productInfo.repaymentPeriod
      ? parseInt(productInfo.repaymentPeriod) * 12
      : 30 * 12;

    const monthlyRate = interest / 12 / 100;
    let calculatedResult;

    switch (productInfo.repaymentType) {
      case 'EQUALPMT':
        // 원리금 균등 분할 상환 계산
        const monthlyPaymentEQUALPMT =
          (amount * monthlyRate * Math.pow(1 + monthlyRate, term)) /
          (Math.pow(1 + monthlyRate, term) - 1);
        const totalPaymentEQUALPMT = monthlyPaymentEQUALPMT * term;
        const interestTotalEQUALPMT = totalPaymentEQUALPMT - amount;

        calculatedResult = {
          monthlyPayment: monthlyPaymentEQUALPMT,
          totalPaymentAmount: totalPaymentEQUALPMT,
          interestTotal: interestTotalEQUALPMT,
        };
        break;

      case 'EQUALAMORT':
        // 원금 균등 분할 상환 계산 (첫 달 상환금액, 총 상환금, 총 이자)
        let totalPaymentEQUALAMORT = amount;
        let interestTotalEQUALAMORT = 0;
        const principalPayment = amount / term;
        const monthlyPaymentFirstMonth =
          principalPayment + amount * monthlyRate;

        for (let i = 1; i <= term; i++) {
          const remainingPrincipal = amount - principalPayment * (i - 1);
          const monthlyInterest = remainingPrincipal * monthlyRate;
          interestTotalEQUALAMORT += monthlyInterest;
        }
        totalPaymentEQUALAMORT += interestTotalEQUALAMORT;

        calculatedResult = {
          monthlyPayment: monthlyPaymentFirstMonth,
          totalPaymentAmount: totalPaymentEQUALAMORT,
          interestTotal: interestTotalEQUALAMORT,
        };
        break;

      case 'FIXEDPMT':
        // 만기 일시 상환 계산
        const monthlyPaymentFIXEDPMT = amount * monthlyRate;
        const interestTotalFIXEDPMT = monthlyPaymentFIXEDPMT * term;
        const totalPaymentFIXEDPMT = amount + interestTotalFIXEDPMT;

        calculatedResult = {
          monthlyPayment: monthlyPaymentFIXEDPMT,
          totalPaymentAmount: totalPaymentFIXEDPMT,
          interestTotal: interestTotalFIXEDPMT,
        };
        break;

      default:
        console.warn(
          `알 수 없는 상환 방식이 감지되어 원리금 균등 방식으로 계산합니다: ${productInfo.repaymentType}`,
        );
        // 기본값으로 원리금 균등 계산을 적용
        const monthlyPaymentDefault =
          (amount * monthlyRate * Math.pow(1 + monthlyRate, term)) /
          (Math.pow(1 + monthlyRate, term) - 1);
        const totalPaymentDefault = monthlyPaymentDefault * term;
        const interestTotalDefault = totalPaymentDefault - amount;

        calculatedResult = {
          monthlyPayment: monthlyPaymentDefault,
          totalPaymentAmount: totalPaymentDefault,
          interestTotal: interestTotalDefault,
          repaymentMethod: '원리금 균등', // 기본값임을 명시
        };
        break;
    }

    setCalcAmount(amount);
    setCalcTerm(term);
    setCalcInterest(interest);

    setCalcRate([
      {
        label: '월 상환 금액',
        text: `${currency(calculatedResult.monthlyPayment)}원`,
      },
      {
        label: '총 상환 금액',
        text: `${currency(calculatedResult.totalPaymentAmount)}원`,
      },
      {
        label: '총 대출 이자',
        text: `${currency(calculatedResult.interestTotal)}원`,
      },
      {
        label: '상환 방식',
        text:
          productInfo.repaymentType === 'EQUALPMT'
            ? '원리금 균등'
            : productInfo.repaymentType === 'EQUALAMORT'
            ? '원금 균등'
            : '만기 일시',
      },
      { label: '예상 금리', text: `${interest}%` },
      { label: '대출금', text: `${addUnitToMaxAmount(amount)}` },
      { label: '최대 대출기간', text: getYearMonthNotation(term) },
    ]);

    setMtgInfo([
      { label: '대출 목적', value: housePreRequestInfoData?.purposeCdNm },
      { label: '주택 주소', value: housePreRequestInfoData?.areaName },
      {
        label: '생애 첫 주택 구매 여부',
        value:
          housePreRequestInfoData?.firstYn === 'Y'
            ? '첫 구매에요'
            : '첫 구매가 아니에요',
      },
      {
        label: '세입자 유무',
        value:
          housePreRequestInfoData?.tenantYn === 'Y'
            ? '세입자 있어요'
            : '세입자 없어요',
      },
      {
        label: '보유 중인 대출 잔액',
        value: currencyKR(housePreRequestInfoData?.previousAmt),
      },
      {
        label: '대출 희망 금액',
        value: currencyKR(housePreRequestInfoData?.hopefulAmt),
      },
    ]);

    const { summary, detail } = getSummaryInfoList();
    setProductOptionsSummary(summary);
    setProductOptionsDetail(detail);

    const doc = getDocument();
    setDoc(doc);
    setStepList([
      { label: '1. 대출 상품 조회', text: '', isIcon: false },
      {
        label: '2. 대출 상담 신청',
        text: `${houseCustProductInfo?.houseProductInfo?.compNm}에서 진행`,
        isIcon: false,
      },
      {
        label: '3. 심사 후 결과 확인',
        text: '',
        isIcon: false,
      },
      {
        label: '4. 서류 제출하기',
        text: '필요한 서류를 확인해보세요',
        isIcon: doc !== '',
      },
      { label: '5. 대출 실행', text: '', isIcon: false },
    ]);
  }, [preDtl]);

  const [bottomsheet, setBottomsheet] = useState<any>({
    isOpen: false,
    title: '',
    close: () => {},
    children: '',
  });
  const openBottomsheet = (val: string) => {
    setBottomsheet((prev: any) => {
      return {
        isOpen: true,
        title: val,
        close: () =>
          setBottomsheet(() => {
            return { ...prev, isOpen: false };
          }),
      };
    });
  };

  const { navigate } = useNav();
  const navigateCallTime = ({
    preRequestDtlNo,
    compNm,
    callNo,
    link,
  }: {
    preRequestDtlNo: number;
    compNm: string;
    callNo: string;
    link: string;
  }) => {
    const callInfo = { preRequestDtlNo, compNm, callNo, link };
    navigate('/calltime', { state: callInfo, replace: true });
  };

  const navigateComplete = () => {
    navigate('/mtg/result-complete', { replace: true });
  };

  const onClickPrimeRate = (item: any, switchOn: boolean) => {
    const primeRateItem = item.item;
    const primeRate = primeRateItem.primeRate;

    if (switchOn) {
      setApplyPrimeRate((prev) => {
        return (Number(prev) + primeRate).toFixed(2);
      });
    } else {
      setApplyPrimeRate((prev) => {
        return (Number(prev) - primeRate).toFixed(2);
      });
    }
  };

  useEffect(() => {}, [applyPrimeRate]);

  const { houseCustProductInfo } = preDtl || {};

  const getSummaryInfoList = () => {
    const summaryList =
      houseCustProductInfo?.houseProductInfo?.productHouseSummaryList;
    if (!summaryList) {
      return {
        summary: [],
        detail: [],
      };
    }

    const summaryInfos: any[] = [];
    const detailInfos: any[] = [];
    summaryList.forEach((item: any) => {
      if (item.summaryExposureYn === 'Y') {
        summaryInfos.push({
          label: item.productCategoryNm,
          value: item.content,
          key: item.productCategoryCode,
          detailExposureYn: 'N',
          order: item.displayOrderSummary,
        });
      }
      if (item.detailExposureYn === 'Y') {
        detailInfos.push({
          title:
            item.productCategoryNm === '공통영역' ? '' : item.productCategoryNm,
          description: item.content
            ? item.content.replace(/\n/g, '<br />')
            : '',
          key: item.productCategoryCode,
        });
      }
    });

    const sortedSummary = summaryInfos.sort((a: any, b: any) => {
      return a.order - b.order;
    });
    if (detailInfos.length > 0) {
      sortedSummary.push({
        label: '상품안내',
        value: '자세히보기',
        key: 'summaryProductDetail',
        detailExposureYn: 'Y',
        order: 9999,
      });
    }

    return {
      summary: sortedSummary,
      detail: detailInfos.sort((a: any, b: any) => {
        return a.order - b.order;
      }),
    };
  };

  const getDocument = () => {
    const doc =
      houseCustProductInfo?.houseProductInfo?.productHouseSummaryList?.filter(
        (summary: any) => summary.productCategoryCode === 'DOCUMENT',
      );
    if (doc && doc.length > 0) {
      return doc[0].content;
    }
    return '';
  };

  const calculatePrimeRate = () => {
    const productInfo = houseCustProductInfo?.houseProductInfo;
    const hasPrimeRate = productInfo?.primeRateYn === 'Y';
    if (!hasPrimeRate) {
      return {
        hasPrimeRate: false,
        maxPrimeRate: 0,
        prefRate: [],
      };
    }
    let maxPrimeRate = 0;
    const prefRate = productInfo.productHousePrimeRateList
      .map((item: any) => {
        maxPrimeRate += item.primeRate;
        return {
          label: item.primeRateConditions,
          text: `금리가 ${item.primeRate}% 낮아져요`,
          order: item.displayOrderPrimeRate,
          switchOn: item.exposureToggleYn === 'Y',
          item: item,
        };
      })
      .sort((a: any, b: any) => {
        return a.order - b.order;
      });

    return {
      hasPrimeRate,
      maxPrimeRate: maxPrimeRate.toFixed(2),
      prefRate,
    };
  };

  const getCtaButtonText = () => {
    const product = houseCustProductInfo?.houseProductInfo;
    if (!product) {
      return '';
    }
    if (product.ctaInactiveYn === 'N') {
      return product.ctaTextBasic;
    }

    if (
      product.ctaInactiveYn === 'Y' &&
      houseCustProductInfo?.houseRequestProductInfo
    ) {
      return product.ctaTextInactive;
    }

    return product.ctaTextBasic;
  };

  return {
    productInfo: houseCustProductInfo?.houseProductInfo,
    custPreProductInfo: houseCustProductInfo?.housePreProductInfo,
    custRequestProductInfo: houseCustProductInfo?.houseRequestProductInfo,
    houseRequestProductInfo: houseCustProductInfo?.houseRequestProductInfo,
    isLoanComplete:
      houseCustProductInfo?.houseRequestProductInfo?.status === '03' &&
      houseCustProductInfo?.houseProductInfo?.loanCancelYn === 'N',
    hashTagList: houseCustProductInfo?.houseProductInfo?.productHouseChipList
      ?.map((item: any) => {
        return {
          type: 'event',
          text: item.displayText,
          backgroundColor: item.backgroundColor,
          borderColor: item.borderColor,
          order: item.displayOrderChip,
        };
      })
      .sort((a: any, b: any) => {
        return a.order - b.order;
      }),
    btnText: getCtaButtonText(),
    primeRate: calculatePrimeRate(),
    calcRate,
    calcAmount,
    calcTerm,
    calcInterest,
    stepList: stepList,
    doc,
    mtgInfo: mtgInfo,
    productOptionsSummary: productOptionsSummary,
    productOptionsDetail: productOptionsDetail,
    bottomsheet,
    openBottomsheet,

    navigateCallTime,
    navigateComplete,

    sendBeaconClkBtn,

    onClickPrimeRate,
  };
};
